import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import './_preview.scss'
import { useTranslation } from 'react-i18next'
import { type InfiniteData, useQueryClient } from 'react-query'
import styled from 'styled-components'
import type SwiperCore from 'swiper'
import { Keyboard, Mousewheel, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import * as SvgIcons from 'assets/svg/sprite'
import AddToPlaylist from 'components/UI/Modal/AddToPlaylist/AddToPlaylist'
import { OptionsDropdown } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { usePreviewContext } from 'context/previewContext'
import { useHomePostsFeed } from 'helpers/hooks'
import { getCachedBlob, prefetchImageToBlob } from 'helpers/mediaPrefetch'
import { enqueuePostView } from 'helpers/trackingViews'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import { useUserProfile } from 'store'
import { useFollowingFeedCacheStore } from 'store/followingFeedCacheStore'
import { usePreviewStore } from 'store/previewStore'
import { type IFile } from 'types/interfaces/IFile'
import { type IHomeFeedCursorPage, type IPost } from 'types/interfaces/ITypes'

import PreviewComments from './PreviewComments/PreviewComments'
import SinglePostPreview from './SinglePostPreview/SinglePostPreview'

import 'swiper/css/bundle'

const PREFETCH_AHEAD = 1
const VIRTUAL_BEFORE = 1
const VIRTUAL_AFTER = 1
const HOME_FEED_PAGE_SIZE = 15
const FETCH_WHEN_REMAINING_POSTS = 2

function clampIndex(idx: number, len: number): number {
  if (!Number.isFinite(idx) || len <= 0) return 0
  return Math.min(Math.max(idx, 0), len - 1)
}

function collectPhotoUrls(post?: IPost | null): string[] {
  if (!post) return []

  const urls: string[] = []
  const photos = Array.isArray((post as any)?.photos) ? (post as any).photos : []
  const previews = Array.isArray((post as any)?.photos_preview)
    ? (post as any).photos_preview
    : []

  photos.forEach((photo: any) => {
    const url = String(photo?.url ?? '').trim()
    if (url) urls.push(url)
  })

  previews.forEach((photo: any) => {
    const url = String(photo?.url ?? photo?.thumb_url ?? '').trim()
    if (url) urls.push(url)
  })

  return Array.from(new Set(urls))
}

function flattenFeed(data?: InfiniteData<IHomeFeedCursorPage>): IPost[] {
  const pages = data?.pages
  if (!Array.isArray(pages)) return []

  return pages
    .flatMap(page => page?.page?.data?.data ?? [])
    .filter(Boolean)
}

function sameFeed(a: IPost[], b: IPost[]): boolean {
  if (a.length !== b.length) return false

  return a.every((post, index) => {
    const other = b[index]

    return (
      other?.id === post.id &&
      other?.updated_at === post.updated_at &&
      other?.is_purchased === post.is_purchased &&
      Number(other?.goal?.tipped ?? 0) ===
        Number(post?.goal?.tipped ?? 0)
    )
  })
}

const PreviewHome = () => {
  const [previewFeed, setPreviewFeed] = useState<IPost[]>([])
  const [showControls, setShowControls] = useState(true)
  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)
  const [allFiles, setAllFiles] = useState<IFile[]>([])
  const [isMediaZoomed, setIsMediaZoomed] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const swiperRef = useRef<SwiperCore | null>(null)
  const lastFetchSigRef = useRef('')
  const viewTimerRef = useRef<number | null>(null)
  const didHydrateFromCacheRef = useRef(false)

  const previewCtx = usePreviewContext()
  if (!previewCtx) return null

  const {
    userId,
    query,
    filterQuery,
    clearModal,
    handleMinimize,
    updateSelectedPost
  } = previewCtx

  const userData = useUserProfile()
  const queryClient = useQueryClient()
  const modalContext = useModalContext()
  const { t } = useTranslation()

  const previewOpen = usePreviewStore(state => state.previewOpen)
  const selectedPost = usePreviewStore(state => state.state.selectedPost)
  const minimized = usePreviewStore(state => state.minimized)
  const commentsActive = usePreviewStore(state => state.commentsActive)
  const selectedPostData = usePreviewStore(state => state.selectedPostData)
  const selectedSlide = usePreviewStore(s => s.state.selectedSlide)
  const setSelectedPostData = usePreviewStore(state => state.setSelectedPostData)
  const setHomeFeedActivePost = usePreviewStore(
    state => state.setHomeFeedActivePost
  )
  const setCommentsActive = usePreviewStore(state => state.setCommentsActive)
  const commentCountByPostId = usePreviewStore(state => state.commentCountByPostId)

  const hasHydrated = useFollowingFeedCacheStore(state => state.hasHydrated)
  const getFeed = useFollowingFeedCacheStore(state => state.getFeed)
  const setFeed = useFollowingFeedCacheStore(state => state.setFeed)

  const isAuthed = Boolean(userData?.id)

  const listId = useMemo(() => {
    const value = filterQuery?.listId
    return value != null ? String(value) : 'all'
  }, [filterQuery?.listId])

  const type = useMemo(() => {
    const value = filterQuery?.type
    return typeof value === 'string' && value.trim() ? value : 'all'
  }, [filterQuery?.type])

  const cacheKey = useMemo(
    () => `${listId}:${type}:full`,
    [listId, type]
  )

  const hasCachedHomeFeed = useMemo(() => {
    if (!query?.length) return false
    return Boolean(queryClient.getQueryData(query))
  }, [query, queryClient])

  const paginationQuery = useHomePostsFeed({
    ...(filterQuery ?? {}),
    list: listId,
    type,
    enabled: isAuthed,
    refetchOnMount: !hasCachedHomeFeed
  })

  const data = paginationQuery?.data
  const fetchNextPage = paginationQuery?.fetchNextPage
  const hasNextPage = paginationQuery?.hasNextPage ?? false
  const isFetchingNextPage = paginationQuery?.isFetchingNextPage ?? false
  const error = paginationQuery?.error
  const status = paginationQuery?.status ?? 'idle'

  const postId = selectedPostData?.id
  const hasPost =
    typeof postId === 'string' &&
    postId.trim().length > 0

  useEffect(() => {
    if (!query?.length) return

    const cached =
      queryClient.getQueryData<InfiniteData<IHomeFeedCursorPage>>(query)

    const posts = flattenFeed(cached)
    if (!posts.length) return

    setPreviewFeed(previous =>
      sameFeed(previous, posts) ? previous : posts
    )
  }, [query, queryClient])

  useEffect(() => {
    if (!hasHydrated) return
    if (didHydrateFromCacheRef.current) return

    const selectedId = selectedPostData?.id

    const reactQueryCached = query?.length
      ? queryClient.getQueryData<InfiniteData<IHomeFeedCursorPage>>(query)
      : undefined

    const reactQueryPosts = flattenFeed(reactQueryCached)

    if (
      reactQueryPosts.length &&
      (!selectedId ||
        reactQueryPosts.some(post => post.id === selectedId))
    ) {
      didHydrateFromCacheRef.current = true

      setPreviewFeed(previous =>
        sameFeed(previous, reactQueryPosts)
          ? previous
          : reactQueryPosts
      )

      return
    }

    const cachedFeed = (getFeed(cacheKey) ?? []) as IPost[]

    if (
      cachedFeed.length &&
      (!selectedId ||
        cachedFeed.some(post => post.id === selectedId))
    ) {
      didHydrateFromCacheRef.current = true

      setPreviewFeed(previous =>
        sameFeed(previous, cachedFeed)
          ? previous
          : cachedFeed
      )
    }
  }, [
    hasHydrated,
    cacheKey,
    query,
    queryClient,
    getFeed,
    selectedPostData?.id
  ])

  useEffect(() => {
    const networkFeed = flattenFeed(data)
    if (!networkFeed.length) return

    const selected =
      usePreviewStore.getState().selectedPostData

    const nextFeed =
      selected?.id && selected.is_purchased
        ? networkFeed.map(post => {
            if (
              post.id !== selected.id ||
              post.is_purchased
            ) {
              return post
            }

            return {
              ...post,
              ...selected,
              is_purchased: true,
              photos: selected.photos,
              videos: selected.videos,
              sounds: selected.sounds,
              photos_preview: selected.photos_preview ?? [],
              videos_preview: selected.videos_preview ?? []
            }
          })
        : networkFeed

    setPreviewFeed(previous =>
      sameFeed(previous, nextFeed)
        ? previous
        : nextFeed
    )

    const cached = (getFeed(cacheKey) ?? []) as IPost[]

    if (!sameFeed(cached, nextFeed)) {
      setFeed(cacheKey, nextFeed)
    }
  }, [
    data,
    cacheKey,
    getFeed,
    setFeed
  ])

  useEffect(() => {
    if (
      !selectedPostData?.id ||
      !selectedPostData.is_purchased
    ) {
      return
    }

    setPreviewFeed(previous => {
      const index = previous.findIndex(
        post => post.id === selectedPostData.id
      )

      if (index < 0) return previous

      const current = previous[index]

      const next = [...previous]

      next[index] = {
        ...current,
        ...selectedPostData,
        is_purchased: true,
        photos_preview:
          selectedPostData.photos_preview ?? [],
        videos_preview:
          selectedPostData.videos_preview ?? []
      }

      return next
    })
  }, [selectedPostData])

  const slidesSource = useMemo<IPost[]>(() => {
    if (!selectedPostData?.id) {
      return previewFeed
    }

    if (
      previewFeed.some(
        post => post.id === selectedPostData.id
      )
    ) {
      return previewFeed
    }

    if (
      typeof selectedPost === 'number' &&
      selectedPost >= 0
    ) {
      const next = [...previewFeed]

      const insertAt = Math.min(
        selectedPost,
        next.length
      )

      next.splice(
        insertAt,
        0,
        selectedPostData
      )

      return next
    }

    return [
      selectedPostData,
      ...previewFeed
    ]
  }, [
    previewFeed,
    selectedPostData,
    selectedPost
  ])

  const slidesWithCommentCounts = useMemo(
    () =>
      slidesSource.map(post => {
        const override =
          commentCountByPostId[String(post.id)]

        return override == null
          ? post
          : {
              ...post,
              comment_count: override
            }
      }),
    [
      slidesSource,
      commentCountByPostId
    ]
  )

  const slidesLen = slidesWithCommentCounts.length
  const isUsingRealFeed = previewFeed.length > 0

  const selectedFeedIndex = useMemo(() => {
    if (!selectedPostData?.id) return -1

    return slidesWithCommentCounts.findIndex(
      post => post.id === selectedPostData.id
    )
  }, [
    slidesWithCommentCounts,
    selectedPostData?.id
  ])

  const initialSlide = useMemo(() => {
    if (!slidesLen) return 0

    if (selectedFeedIndex >= 0) {
      return selectedFeedIndex
    }

    return clampIndex(
      typeof selectedPost === 'number'
        ? selectedPost
        : 0,
      slidesLen
    )
  }, [
    slidesLen,
    selectedFeedIndex,
    selectedPost
  ])

  useEffect(() => {
    const swiper = swiperRef.current
    if (!swiper) return

    const enabled = !isMediaZoomed

    swiper.allowTouchMove = enabled
    swiper.allowSlideNext = enabled
    swiper.allowSlidePrev = enabled
  }, [isMediaZoomed])

useLayoutEffect(() => {
  if (!previewOpen) return

  const swiper = swiperRef.current

  if (
    !swiper ||
    swiper.destroyed ||
    selectedFeedIndex < 0
  ) {
    return
  }

  if (swiper.activeIndex !== selectedFeedIndex) {
    swiper.slideTo(selectedFeedIndex, 0, false)
  }

  setActiveIndex(prev =>
    prev === selectedFeedIndex
      ? prev
      : selectedFeedIndex
  )
}, [previewOpen, selectedFeedIndex])

  useEffect(() => {
    const swiper = swiperRef.current
    if (!swiper || !slidesLen) return

    requestAnimationFrame(() => {
      swiper.virtual?.update?.(true)
    })
  }, [slidesLen])

  const maybeFetchNext = useCallback(
    (nextActiveIndex: number) => {
      if (!fetchNextPage) return
      if (!hasNextPage) return
      if (isFetchingNextPage) return
      if (!slidesLen) return

      if (slidesLen < HOME_FEED_PAGE_SIZE) {
        return
      }

      const remaining =
        slidesLen - 1 - nextActiveIndex

      if (
        remaining >
        FETCH_WHEN_REMAINING_POSTS
      ) {
        return
      }

      const signature =
        `${nextActiveIndex}:${slidesLen}`

      if (
        lastFetchSigRef.current ===
        signature
      ) {
        return
      }

      lastFetchSigRef.current =
        signature

      void fetchNextPage()
    },
    [
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      slidesLen
    ]
  )

  const prefetchSignature = useMemo(() => {
    if (!slidesLen) return 'empty'

    const index =
      clampIndex(activeIndex, slidesLen)

    const ids: Array<string | number> = []

    const current =
      slidesWithCommentCounts[index]

    if (current?.id) {
      ids.push(current.id)
    }

    for (
      let step = 1;
      step <= PREFETCH_AHEAD;
      step++
    ) {
      const next =
        slidesWithCommentCounts[
          index + step
        ]

      if (next?.id) {
        ids.push(next.id)
      }
    }

    return `${index}:${ids.join(',')}`
  }, [
    slidesWithCommentCounts,
    slidesLen,
    activeIndex
  ])

  useEffect(() => {
    if (!slidesLen) return

    const index =
      clampIndex(activeIndex, slidesLen)

    const controller =
      new AbortController()

    const urls =
      new Set<string>()

    collectPhotoUrls(
      slidesWithCommentCounts[index]
    ).forEach(url => urls.add(url))

    for (
      let step = 1;
      step <= PREFETCH_AHEAD;
      step++
    ) {
      const next =
        slidesWithCommentCounts[
          index + step
        ]

      if (next) {
        collectPhotoUrls(next)
          .forEach(url => urls.add(url))
      }
    }

    urls.forEach(url => {
      if (!url || getCachedBlob(url)) {
        return
      }

      prefetchImageToBlob(
        url,
        controller.signal
      ).catch(() => {})
    })

    return () => {
      controller.abort()
    }
  }, [
    prefetchSignature,
    slidesWithCommentCounts,
    slidesLen,
    activeIndex
  ])

  useEffect(() => {
    if (!selectedPostData) {
      setAllFiles([])
      return
    }

    emitter.emit(
      EventsEmitter.STOP_ALL_AUDIO_POSTS
    )

    if (!selectedPostData.is_purchased) {
      setAllFiles([])
      return
    }

    const files: IFile[] = [
      ...(selectedPostData.photos ?? []),
      ...(selectedPostData.videos ?? []),
      ...(selectedPostData.sounds ?? [])
    ].sort(
      (a, b) =>
        (a?.order ?? 0) -
        (b?.order ?? 0)
    )

    setAllFiles(files)
  }, [selectedPostData])

  useEffect(() => {
    if (!isAuthed) return
    if (!hasPost) return
    if (!userData?.id) return
    if (!postId) return

    const creatorId =
      selectedPostData?.user_id ??
      selectedPostData?.user?.id

    if (
      !creatorId ||
      creatorId === userData.id
    ) {
      return
    }

    if (viewTimerRef.current != null) {
      window.clearTimeout(
        viewTimerRef.current
      )

      viewTimerRef.current = null
    }

    viewTimerRef.current =
      window.setTimeout(() => {
        enqueuePostView(postId)
      }, 1000)

    return () => {
      if (
        viewTimerRef.current != null
      ) {
        window.clearTimeout(
          viewTimerRef.current
        )

        viewTimerRef.current = null
      }
    }
  }, [
    isAuthed,
    hasPost,
    postId,
    userData?.id,
    selectedPostData?.user_id,
    selectedPostData?.user?.id
  ])


  const openOptions = useCallback(() => {
    window.setTimeout(
      () => setIsOptionsNavOpen(true),
      0
    )
  }, [])

  const viewerOptions = useMemo(() => {
    if (!hasPost) return []

    return [
      {
        icon: (
          <SvgIcons.IconOptionsPlaylist />
        ),
        text: t('addToList'),
        disabled: allFiles.length === 0,
        action: () => {
          setIsOptionsNavOpen(false)

          modalContext.addModal(
            '',
            <AddToPlaylist
              itemId={postId}
              itemType='post'
              closeFn={() => modalContext.clearModal()}
            />,
            true,
            true
          )
        }
      }
    ]
  }, [
    hasPost,
    postId,
    t,
    allFiles.length,
    modalContext
  ])

  const handleSlideChange = useCallback(
    (swiper: SwiperCore) => {
      const nextIndex =
        swiper.activeIndex ?? 0

      setActiveIndex(nextIndex)
      maybeFetchNext(nextIndex)

      const currentPost =
        slidesWithCommentCounts[
          nextIndex
        ]

      if (!currentPost?.id) return

      if (
        currentPost.id !==
        selectedPostData?.id
      ) {
        setHomeFeedActivePost(
          currentPost,
          nextIndex
        )
      }
    },
    [
      slidesWithCommentCounts,
      maybeFetchNext,
      selectedPostData?.id,
      setSelectedPostData
    ]
  )

  if (error) {
    console.log(error)
  }

  if (status === 'error') {
    return (
      <p>
        Error: {(error as any)?.message}
      </p>
    )
  }

  return (
    <div className='preview__wrapper'>
      {hasPost &&
        isOptionsNavOpen && (
          <OptionsDropdown
            role=''
            content='post'
            isOptionsNavOpen={
              isOptionsNavOpen
            }
            setIsOptionsNavOpen={
              setIsOptionsNavOpen
            }
            postId={postId}
            options={viewerOptions}
            deleteFn={() => {
              if (
                isOptionsNavOpen
              ) {
                setIsOptionsNavOpen(
                  false
                )
              }

              modalContext.clearModal()
              clearModal()
            }}
            withoutBottomNavigation
          />
        )}

      {slidesLen > 0 && (
        <Swiper
          allowTouchMove={!isMediaZoomed}
          modules={[
            Keyboard,
            Virtual,
            Mousewheel
          ]}
          mousewheel={{
            enabled: true,
            forceToAxis: true,
            sensitivity: 0.5,
            releaseOnEdges: true
          }}
          virtual={{
            enabled: true,
            addSlidesBefore:
              VIRTUAL_BEFORE,
            addSlidesAfter:
              VIRTUAL_AFTER
          }}
          onInit={(
            swiper: SwiperCore
          ) => {
            swiperRef.current =
              swiper

            swiper.el?.setAttribute(
              'tabindex',
              '0'
            )

            swiper.el?.focus()

            const target =
              clampIndex(
                initialSlide,
                slidesLen
              )

            if (
              swiper.activeIndex !==
              target
            ) {
              swiper.slideTo(
                target,
                0,
                false
              )
            }

            setActiveIndex(target)

            requestAnimationFrame(
              () => {
                swiper.virtual
                  ?.update?.(true)
              }
            )
          }}
          initialSlide={initialSlide}
          style={{
            width: '100%',
            height: '100%'
          }}
          className='mySwiper'
          longSwipes={false}
          longSwipesRatio={0.25}
          shortSwipes
          direction='vertical'
          speed={500}
          keyboard={{
            enabled: true,
            onlyInViewport: true
          }}
          touchStartPreventDefault
          passiveListeners={false}
          resistanceRatio={0.85}
          touchReleaseOnEdges
          slidesPerView={1}
          slidesPerGroup={1}
          centeredSlides
          pagination={false}
          onSlideChangeTransitionStart={() => {
            if (!showControls) {
              setShowControls(true)
            }
          }}
          onSlideChange={
            handleSlideChange
          }
          noSwiping={
            !showControls ||
            commentsActive ||
            minimized
          }
        >
          {slidesWithCommentCounts.map(
            (post, index) => (
              <SwiperSlide
                key={post?.id ?? index}
                virtualIndex={index}
                className={
                  minimized
                    ? 'swiper-no-swiping'
                    : ''
                }
              >
                <PostPreviewWrapper>
                  
                  <SinglePostPreview
                    post={post}
                    selectedSlide={
                      post?.id === selectedPostData?.id
                        ? selectedSlide ?? 0
                        : 0
                    }
                    showControls={
                      showControls
                    }
                    onZoomStateChange={
                      setIsMediaZoomed
                    }
                    setIsOptionsNavOpen={
                      setIsOptionsNavOpen
                    }
                    onOpenOptions={
                      openOptions
                    }
                    minimizeFn={(
                      fileIndex: number,
                      mediaType: string
                    ) => {
                      updateSelectedPost(
                        index,
                        fileIndex,
                        mediaType,
                        post
                      )

                      if (
                        minimized &&
                        !showControls
                      ) {
                        setShowControls(
                          true
                        )
                      }

                      handleMinimize(
                        !minimized
                      )
                    }}
                    toggleControls={() =>
                      setShowControls(
                        value => !value
                      )
                    }
                    isFirstPost={
                      index === 0
                    }
                    hasNextPage={
                      !!hasNextPage
                    }
                    isInViewport={
                      post.id ===
                      selectedPostData?.id
                    }
                    isLastPost={
                      isUsingRealFeed &&
                      index ===
                        previewFeed.length -
                          1
                    }
                  />
                </PostPreviewWrapper>
              </SwiperSlide>
            )
          )}
        </Swiper>
      )}

      {selectedPostData &&
        !selectedPostData
          .stories?.length && (
          <PreviewComments
            commentsActive={
              commentsActive
            }
            setCommentsActive={
              setCommentsActive
            }
            activeCommentsPostId={
              selectedPostData.id
            }
            commentCount={
              commentCountByPostId[
                selectedPostData.id
              ] ??
              selectedPostData
                .comment_count
            }
            isMyPost={
              userData?.id ===
              selectedPostData
                .user?.id
            }
          />
        )}
    </div>
  )
}

export default PreviewHome

const PostPreviewWrapper = styled.div`
  height: 100%;
  min-height: 100%;
  max-height: 100%;
`